import { useRegisterCommands } from "components/Layout/Panels/VSCode/useRegisterCommands";
import { usePitcher } from "features/utils/usePitcher";
import { useCapabilities } from "hooks/pitcher/useCapabilities";
import { useConfiguredTasks } from "hooks/pitcher/useConfiguredTasks";
import { usePermissions } from "hooks/pitcher/usePermissions";
import { partition } from "lodash";

import { createRegistrator } from "commands/register";
import { executeVSCodeCommand } from "utils/vscode";

const CONFIGURE_TASK_LABEL = "Create & Run New Task...";

const useRegisterNewTaskPaletteCommands = (): void => {
  const pitcherPermissions = usePermissions();
  const pitcherCapabilities = useCapabilities();
  const pitcher = usePitcher();

  const configuredTasks = useConfiguredTasks();

  const canCreateTasks =
    pitcherCapabilities.task?.create && pitcherPermissions.task?.create;

  useRegisterCommands(
    [
      {
        id: "codesandbox.tasks.run",
        name: "Run/Open Task...",
        section: "CodeSandbox Tasks",
        perform: async (vscode) => {
          const [running, notRunning] = partition(configuredTasks, (task) => {
            return task.shell && task.shell.status === "RUNNING";
          });
          const taskToItem = (task: (typeof running)[0]) => {
            const item = {
              label: task.name,
              detail: task.command,
              task: task,
            };
            return item;
          };

          const items = [
            { kind: -1, label: "" },
            ...notRunning.map(taskToItem),
            { kind: -1, label: "Running Tasks" },
            ...running.map(taskToItem),
          ];
          if (canCreateTasks) {
            items.push({ kind: -1, label: "Configuration" });
            items.push({ kind: 0, label: CONFIGURE_TASK_LABEL });
          }

          const chosenItem = await vscode.window.showQuickPick(items, {
            placeHolder: "Select a task to run or open",
          });

          if (chosenItem) {
            if ("task" in chosenItem) {
              const task = chosenItem.task;
              // If task is not running, run it
              if (!task.shell || task.shell.status !== "RUNNING") {
                pitcher.clients.task.runTask(task.id);
              }
            } else if (chosenItem.label === CONFIGURE_TASK_LABEL) {
              executeVSCodeCommand("codesandbox.tasks.createTask");
            }
          }
        },
      },
    ],
    [configuredTasks, canCreateTasks],
  );
};

export const RegisterNewTaskPaletteCommands = createRegistrator(
  useRegisterNewTaskPaletteCommands,
);
